import React from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiX, FiAlertTriangle, FiTrash2 } from 'react-icons/fi';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--background-overlay);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled(motion.div)`
  background-color: var(--background-primary);
  border-radius: 0;
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const WarningIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 0;
  background-color: rgba(252, 129, 129, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--error-color);
`;

const HeaderContent = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  color: var(--text-primary);
  font-family: 'Inter', sans-serif;
  font-size: 1.25rem;
  font-weight: 500;
  margin: 0 0 0.25rem 0;
`;

const ModalSubtitle = styled.p`
  color: var(--text-muted);
  font-size: 0.875rem;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: var(--text-primary);
    background-color: var(--background-secondary);
  }
`;

const Content = styled.div`
  margin-bottom: 2rem;
`;

const WarningMessage = styled.div`
  background-color: rgba(246, 224, 94, 0.1);
  border: 1px solid var(--warning-color);
  border-radius: 0;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const WarningTitle = styled.div`
  color: var(--warning-dark);
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const WarningText = styled.div`
  color: var(--warning-dark);
  font-size: 0.875rem;
  line-height: 1.5;
`;

const ItemDetails = styled.div`
  background-color: var(--background-secondary);
  border: 1px solid var(--border-color);
  border-radius: 0;
  padding: 1rem;
`;

const ItemName = styled.div`
  color: var(--text-primary);
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
`;

const ItemType = styled.div`
  color: var(--text-muted);
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  justify-content: center;
  min-height: 44px;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CancelButton = styled(Button)`
  background-color: var(--background-tertiary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--background-secondary);
    border-color: var(--border-dark);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
`;

const DeleteButton = styled(Button)`
  background-color: var(--error-color);
  color: var(--text-inverse);
  
  &:hover:not(:disabled) {
    background-color: var(--error-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

function DeleteConfirmationModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  item, 
  itemType, 
  loading = false,
  title,
  message,
  confirmButtonText
}) {
  const getItemTypeLabel = (type) => {
    switch (type) {
      case 'user':
        return 'User';
      case 'course':
        return 'Course';
      case 'user password':
        return 'User';
      default:
        return 'Item';
    }
  };

  const getWarningMessage = (type) => {
    switch (type) {
      case 'user':
        return 'This action will permanently delete the user and all their associated data including enrollments, submissions, and grades.';
      case 'course':
        return 'This action will permanently delete the course and all associated assignments, enrollments, and submissions.';
      default:
        return 'This action cannot be undone.';
    }
  };

  const getItemName = (item, type) => {
    if (!item) return 'Unknown Item';
    
    switch (type) {
      case 'user':
        return `${item.first_name || ''} ${item.last_name || ''} (${item.email || ''})`;
      case 'user password':
        return `${item.first_name || ''} ${item.last_name || ''} (${item.email || ''})`;
      case 'course':
        return `${item.code} - ${item.name}`;
      default:
        return item.name || item.title || 'Unknown Item';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <ModalOverlay
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <ModalContent
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader>
              <WarningIcon>
                <FiAlertTriangle size={24} />
              </WarningIcon>
              <HeaderContent>
                <ModalTitle>{title || `Delete ${getItemTypeLabel(itemType)}`}</ModalTitle>
                <ModalSubtitle>{message || 'Are you sure you want to proceed?'}</ModalSubtitle>
              </HeaderContent>
              <CloseButton onClick={onClose}>
                <FiX size={20} />
              </CloseButton>
            </ModalHeader>

            <Content>
              {!message && (
                <WarningMessage>
                  <WarningTitle>
                    <FiAlertTriangle size={16} />
                    Warning
                  </WarningTitle>
                  <WarningText>
                    {getWarningMessage(itemType)}
                  </WarningText>
                </WarningMessage>
              )}

              <ItemDetails>
                <ItemName>{getItemName(item, itemType)}</ItemName>
                <ItemType>{getItemTypeLabel(itemType)}</ItemType>
              </ItemDetails>
            </Content>

            <ButtonGroup>
              <CancelButton onClick={onClose} disabled={loading}>
                Cancel
              </CancelButton>
              <DeleteButton onClick={onConfirm} disabled={loading}>
                {loading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: 0,
                      animation: 'spin 1s linear infinite'
                    }} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <FiTrash2 size={16} />
                    {confirmButtonText || `Delete ${getItemTypeLabel(itemType)}`}
                  </>
                )}
              </DeleteButton>
            </ButtonGroup>
          </ModalContent>
        </ModalOverlay>
      )}
    </AnimatePresence>
  );
}

export default DeleteConfirmationModal;
